/**
 * BlockPalette Surface Component
 * Block editor palette for slash commands
 */

import React, { useState } from 'react';
import { Stack, Text, Group, Badge, Paper, TextInput, ScrollArea, UnstyledButton } from '@mantine/core';
import { IconHeading, IconList, IconCheckbox, IconCode, IconPhoto, IconVideo, IconTable } from '@tabler/icons-react';

interface BlockPaletteProps {
  phase?: number;
}

const blockTypes = [
  { id: 'heading1', label: 'Heading 1', icon: IconHeading, description: 'Large section heading', shortcut: '#' },
  { id: 'heading2', label: 'Heading 2', icon: IconHeading, description: 'Medium section heading', shortcut: '##' },
  { id: 'bullet', label: 'Bulleted List', icon: IconList, description: 'Create a bulleted list', shortcut: '-' },
  { id: 'numbered', label: 'Numbered List', icon: IconList, description: 'Create a numbered list', shortcut: '1.' },
  { id: 'todo', label: 'To-do', icon: IconCheckbox, description: 'Track tasks with checkboxes', shortcut: '[]' },
  { id: 'code', label: 'Code Block', icon: IconCode, description: 'Display code snippet', shortcut: '```' },
  { id: 'image', label: 'Image', icon: IconPhoto, description: 'Upload or embed an image', shortcut: '!' },
  { id: 'video', label: 'Video', icon: IconVideo, description: 'Embed a video', shortcut: 'vid' },
  { id: 'table', label: 'Table', icon: IconTable, description: 'Add a table', shortcut: 'table' }
];

const BlockPalette: React.FC<BlockPaletteProps> = ({ phase = 3 }) => {
  const [search, setSearch] = useState('');

  const filteredBlocks = blockTypes.filter(block =>
    block.label.toLowerCase().includes(search.toLowerCase()) ||
    block.shortcut.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Stack gap="md" p="sm">
      <Group justify="space-between" align="center">
        <Text fw={600} size="sm">Block Palette</Text>
        <Badge size="xs" variant="light">Phase {phase}</Badge>
      </Group>

      <TextInput
        placeholder="Search blocks..."
        size="xs"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <ScrollArea h={300}>
        <Stack gap={4}>
          {filteredBlocks.map(block => {
            const Icon = block.icon;
            return (
              <UnstyledButton
                key={block.id}
                p="xs"
                style={{
                  borderRadius: 4,
                  border: '1px solid var(--mantine-color-gray-3)',
                  '&:hover': { backgroundColor: 'var(--mantine-color-gray-1)' }
                }}
              >
                <Group gap="sm">
                  <Icon size={18} style={{ color: 'var(--mantine-color-blue-6)' }} />
                  <div style={{ flex: 1 }}>
                    <Text size="sm" fw={500}>{block.label}</Text>
                    <Text size="xs" c="dimmed">{block.description}</Text>
                  </div>
                  <Badge size="xs" variant="outline">{block.shortcut}</Badge>
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};

export default BlockPalette;